import { appConfig, type AppConfig } from "../config";
import type { ProfileId, ProfilePublicName } from "../content/types";
import { isPersonalizedRecipeRoute, type PersonalizedRecipeRoute } from "../quiz/personalization/recipe-route";

export const ANALYTICS_EVENT_NAMES = [
  "FunnelPageView",
  "QuizStart",
  "QuestionView",
  "QuestionAnswered",
  "Quiz25",
  "Quiz50",
  "Quiz75",
  "QuizComplete",
  "RecipePreviewView",
  "RecipeOpen",
  "LeadFormView",
  "Lead",
  "ResultView",
  "WhatsAppOptIn",
  "OfferBridgeView",
  "OfferView",
  "OfferScroll50",
  "OfferScroll90",
  "OfferClick",
  "MonthlyPlanClick",
  "AnnualPlanClick",
  "InitiateCheckout",
  "Purchase",
  "QuizAbandon",
  "ReturnSession",
] as const;

export type AnalyticsEventName = (typeof ANALYTICS_EVENT_NAMES)[number];

export interface AttributionInput {
  referrer?: string;
  landingUrl?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  fbclid?: string;
  gclid?: string;
  fbp?: string;
  fbc?: string;
  siteSourceName?: string;
  campaignId?: string;
  adsetId?: string;
  adId?: string;
  placement?: string;
}

export interface TrackingConsentInput {
  analytics: boolean;
  marketing: boolean;
  policyVersion: string;
  attribution?: AttributionInput;
}

export interface CreateSessionInput {
  anonymousId: string;
  funnelVariant?: "A" | "B";
  landingEventId?: string;
  attribution?: AttributionInput;
  consent: TrackingConsentInput;
  turnstileToken?: string;
  website?: string;
}

export interface FunnelPageViewInput {
  eventId: string;
  anonymousId: string;
  variant: "A" | "B";
  occurredAt?: string;
  attribution?: AttributionInput;
  consent: TrackingConsentInput;
}

export interface FunnelPageViewResponse {
  accepted: boolean;
  idempotent: boolean;
}

export interface ExperimentVariants {
  landing: "A" | "B";
  leadCapture: "A" | "B";
  offer: "A" | "B";
}

export interface CreateSessionResponse {
  sessionId: string;
  sessionToken: string;
  expiresAt: string;
  currentStep: number;
  variants: ExperimentVariants;
}

export interface SessionAnswer {
  questionId: string;
  answerIds: string[];
  answerVersion: string;
}

export interface SessionResponse {
  id: string;
  status: "started" | "in_progress" | "completed" | "converted" | "abandoned";
  currentStep: number;
  variants: ExperimentVariants;
  answers: SessionAnswer[];
  resultReady: boolean;
}

export interface SaveAnswerInput {
  answerIds: string[];
  answerVersion: string;
  currentStep: number;
}

export interface SaveAnswerResponse {
  saved: true;
  currentStep: number;
}

export interface CompleteSessionResponse {
  resultToken: string;
  expiresAt: string;
  idempotent: boolean;
  route?: PersonalizedRecipeRoute;
}

export interface CaptureLeadInput {
  firstName: string;
  email: string;
  whatsapp?: string;
  emailConsent: boolean;
  whatsappConsent: boolean;
  privacyVersion: string;
  eventId: string;
  turnstileToken?: string;
  website?: string;
}

export interface CaptureLeadResponse {
  leadId: string;
  eventId: string;
  resultToken: string;
  resultUrl: string;
  preferenceUrl: string;
  preferenceTokenExpiresAt: string;
  idempotent: boolean;
}

export interface UpdateWhatsAppInput {
  whatsapp: string;
  consent: boolean;
  policyVersion: string;
  eventId: string;
  website?: string;
  turnstileToken?: string;
}

export interface UpdateWhatsAppResponse {
  updated: true;
  whatsappConsent: boolean;
}

export type PublicAnalyticsMetadata = Record<string, string | number | boolean>;

export interface AnalyticsEventInput {
  eventId: string;
  eventName: AnalyticsEventName;
  pagePath?: string;
  occurredAt?: string;
  metadata?: PublicAnalyticsMetadata;
}

export interface IngestEventsResponse {
  accepted: number;
  duplicates: number;
  analyticsConsent: boolean;
}

export interface PublicProfile {
  id: ProfileId;
  slug: string;
  publicName: ProfilePublicName;
  crmTag: string;
}

export interface ResultProfile extends PublicProfile {
  affinity: number;
}

export interface ResultResponse {
  funnelVariant: "A" | "B";
  primary: ResultProfile;
  secondary: ResultProfile;
  personalization: {
    foodStyle: string;
    considerations: string[];
    answers: Record<string, string[]>;
  };
  route?: PersonalizedRecipeRoute;
  quizVersion: string;
  algorithmVersion: string;
  expiresAt: string;
}

export interface OfferResponse {
  profile: PublicProfile;
  variant: "A" | "B";
  checkoutAvailable: boolean;
}

export interface CreateCheckoutInput {
  planId: string;
  eventId: string;
  resultToken: string;
}

export interface CreateCheckoutResponse {
  checkoutId: string;
  checkoutUrl: string;
  reference: string;
}

export type CheckoutPlan = "monthly" | "annual";

export interface CreateCheckoutIntentInput {
  plan: CheckoutPlan;
  eventId: string;
  resultToken: string;
}

export interface CreateCheckoutIntentResponse {
  checkoutId: string;
  intentId: string;
  checkoutUrl: string;
  reference: string;
  eventId: string;
  idempotent: boolean;
}

export interface CheckoutStatusResponse {
  status: "initiated" | "pending" | "approved" | "failed" | "refunded";
  purchasedAt: string | null;
  amountMinor: number | null;
  currency: string | null;
  purchaseEventId: string | null;
  handoffUrl: string | null;
}

export interface PreferenceResponse {
  emailConsent: boolean;
  whatsappConsent: boolean;
  privacyVersion: string;
  deleted: boolean;
}

export interface PreferenceUpdateInput {
  emailConsent: boolean;
  whatsappConsent: boolean;
  policyVersion: string;
}

export interface PreferenceUpdateResponse {
  emailConsent: boolean;
  whatsappConsent: boolean;
}

export interface DeleteLeadResponse {
  requestId: string;
  status: "completed";
}

export interface AdminMetricsResponse {
  range: { from: string; to: string };
  overview: Record<string, string | number | null>;
  profiles: Array<Record<string, string | number | null>>;
  campaigns: Array<Record<string, string | number | null>>;
  abandonment: Array<Record<string, string | number | null>>;
  experiments: Array<Record<string, string | number | null>>;
}

interface ApiErrorPayload {
  error?: {
    code?: unknown;
    requestId?: unknown;
  };
}

export interface ApiRequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  token?: string;
  body?: unknown;
  signal?: AbortSignal;
  timeoutMs?: number;
  keepalive?: boolean;
  headers?: Readonly<Record<string, string>>;
}

export class ApiClientError extends Error {
  readonly code: string;

  constructor(message: string, code: string, options?: ErrorOptions) {
    super(message, options);
    this.name = "ApiClientError";
    this.code = code;
  }
}

export class ApiTimeoutError extends ApiClientError {
  readonly timeoutMs: number;

  constructor(timeoutMs: number, options?: ErrorOptions) {
    super(`API request timed out after ${timeoutMs}ms`, "request_timeout", options);
    this.name = "ApiTimeoutError";
    this.timeoutMs = timeoutMs;
  }
}

export class ApiNetworkError extends ApiClientError {
  constructor(options?: ErrorOptions) {
    super("The API could not be reached", "network_error", options);
    this.name = "ApiNetworkError";
  }
}

export class ApiAbortedError extends ApiClientError {
  constructor(options?: ErrorOptions) {
    super("The API request was aborted", "request_aborted", options);
    this.name = "ApiAbortedError";
  }
}

export class ApiProtocolError extends ApiClientError {
  readonly endpoint: string;

  constructor(endpoint: string, message: string, options?: ErrorOptions) {
    super(`Invalid API response from ${endpoint}: ${message}`, "invalid_response", options);
    this.name = "ApiProtocolError";
    this.endpoint = endpoint;
  }
}

export class ApiHttpError extends ApiClientError {
  readonly status: number;
  readonly requestId?: string;

  constructor(status: number, code: string, requestId?: string) {
    super(`API request failed with ${status} (${code})`, code);
    this.name = "ApiHttpError";
    this.status = status;
    if (requestId) this.requestId = requestId;
  }
}

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
const UUID_V4_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export interface ApiClientOptions {
  baseUrl?: string;
  timeoutMs?: number;
  fetch?: FetchLike;
}

function makeRequestId(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function record(value: unknown, endpoint: string): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new ApiProtocolError(endpoint, "expected a JSON object");
  }
  return value as Record<string, unknown>;
}

function stringField(value: Record<string, unknown>, key: string, endpoint: string): string {
  if (typeof value[key] !== "string" || value[key] === "") {
    throw new ApiProtocolError(endpoint, `missing string field ${key}`);
  }
  return value[key];
}

function numberField(value: Record<string, unknown>, key: string, endpoint: string): number {
  if (typeof value[key] !== "number" || !Number.isFinite(value[key])) {
    throw new ApiProtocolError(endpoint, `missing numeric field ${key}`);
  }
  return value[key];
}

function booleanField(value: Record<string, unknown>, key: string, endpoint: string): boolean {
  if (typeof value[key] !== "boolean") {
    throw new ApiProtocolError(endpoint, `missing boolean field ${key}`);
  }
  return value[key];
}

function nullableStringField(value: Record<string, unknown>, key: string, endpoint: string): string | null {
  if (value[key] === null) return null;
  return stringField(value, key, endpoint);
}

function nullableNumberField(value: Record<string, unknown>, key: string, endpoint: string): number | null {
  if (value[key] === null) return null;
  return numberField(value, key, endpoint);
}

function variants(value: unknown, endpoint: string): ExperimentVariants {
  const object = record(value, endpoint);
  const landing = stringField(object, "landing", endpoint);
  const leadCapture = stringField(object, "leadCapture", endpoint);
  const offer = stringField(object, "offer", endpoint);
  if (![landing, leadCapture, offer].every((variant) => variant === "A" || variant === "B")) {
    throw new ApiProtocolError(endpoint, "invalid experiment variant");
  }
  return { landing, leadCapture, offer } as ExperimentVariants;
}

function publicProfile(value: unknown, endpoint: string): PublicProfile {
  const object = record(value, endpoint);
  const id = stringField(object, "id", endpoint);
  if (!/^[A-F]$/.test(id)) throw new ApiProtocolError(endpoint, "invalid profile id");
  return {
    id: id as ProfileId,
    slug: stringField(object, "slug", endpoint),
    publicName: stringField(object, "publicName", endpoint) as ProfilePublicName,
    crmTag: stringField(object, "crmTag", endpoint),
  };
}

function metricRecord(value: unknown, endpoint: string): Record<string, string | number | null> {
  const object = record(value, endpoint);
  const output: Record<string, string | number | null> = {};
  for (const [key, item] of Object.entries(object)) {
    if (typeof item !== "string" && typeof item !== "number" && item !== null) {
      throw new ApiProtocolError(endpoint, `invalid metric field ${key}`);
    }
    output[key] = item;
  }
  return output;
}

function metricRows(value: unknown, key: string, endpoint: string): Array<Record<string, string | number | null>> {
  if (!Array.isArray(value)) throw new ApiProtocolError(endpoint, `missing metrics array ${key}`);
  return value.map((item) => metricRecord(item, endpoint));
}

function assertCredential(value: string, name: string): void {
  if (!value.trim()) throw new ApiClientError(`${name} is required`, "missing_credential");
}

function assertEventId(value: string): void {
  if (!UUID_V4_PATTERN.test(value)) {
    throw new ApiClientError("eventId must be an RFC 4122 UUID v4", "invalid_event_id");
  }
}

export class ApiClient {
  readonly baseUrl: string;
  readonly timeoutMs: number;
  private readonly fetcher: FetchLike;

  constructor(options: ApiClientOptions = {}) {
    this.baseUrl = (options.baseUrl ?? appConfig.apiBaseUrl).replace(/\/$/, "");
    this.timeoutMs = options.timeoutMs ?? appConfig.apiTimeoutMs;
    this.fetcher = options.fetch ?? globalThis.fetch.bind(globalThis);
  }

  async request(endpoint: string, options: ApiRequestOptions = {}): Promise<unknown> {
    const timeoutMs = options.timeoutMs ?? this.timeoutMs;
    const controller = new AbortController();
    let timedOut = false;
    const onAbort = () => controller.abort(options.signal?.reason);
    if (options.signal?.aborted) controller.abort(options.signal.reason);
    else options.signal?.addEventListener("abort", onAbort, { once: true });
    const timer = globalThis.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);

    const headers = new Headers(options.headers);
    headers.set("Accept", "application/json");
    headers.set("X-Request-Id", makeRequestId());
    if (options.body !== undefined) headers.set("Content-Type", "application/json");
    if (options.token) headers.set("Authorization", `Bearer ${options.token}`);

    let response: Response;
    try {
      response = await this.fetcher(`${this.baseUrl}${endpoint}`, {
        method: options.method ?? "GET",
        headers,
        credentials: "same-origin",
        signal: controller.signal,
        ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
        ...(options.keepalive !== undefined ? { keepalive: options.keepalive } : {}),
      });
    } catch (error) {
      if (timedOut) throw new ApiTimeoutError(timeoutMs, { cause: error });
      if (options.signal?.aborted) throw new ApiAbortedError({ cause: error });
      throw new ApiNetworkError({ cause: error });
    } finally {
      globalThis.clearTimeout(timer);
      options.signal?.removeEventListener("abort", onAbort);
    }

    let payload: unknown;
    const contentType = response.headers.get("Content-Type") ?? "";
    if (contentType.includes("application/json")) {
      try {
        payload = await response.json();
      } catch (error) {
        throw new ApiProtocolError(endpoint, "malformed JSON", { cause: error });
      }
    } else if (response.status !== 204) {
      try {
        payload = await response.text();
      } catch (error) {
        throw new ApiProtocolError(endpoint, "unreadable response body", { cause: error });
      }
    }

    if (!response.ok) {
      const errorPayload = payload && typeof payload === "object" ? payload as ApiErrorPayload : {};
      const code = typeof errorPayload.error?.code === "string"
        ? errorPayload.error.code
        : `http_${response.status}`;
      const responseRequestId = typeof errorPayload.error?.requestId === "string"
        ? errorPayload.error.requestId
        : response.headers.get("X-Request-Id") ?? undefined;
      throw new ApiHttpError(response.status, code, responseRequestId);
    }
    if (!contentType.includes("application/json")) {
      throw new ApiProtocolError(endpoint, "expected application/json");
    }
    return payload;
  }

  async createSession(input: CreateSessionInput, signal?: AbortSignal): Promise<CreateSessionResponse> {
    const endpoint = "/sessions";
    const object = record(await this.request(endpoint, { method: "POST", body: input, ...(signal ? { signal } : {}) }), endpoint);
    return {
      sessionId: stringField(object, "sessionId", endpoint),
      sessionToken: stringField(object, "sessionToken", endpoint),
      expiresAt: stringField(object, "expiresAt", endpoint),
      currentStep: numberField(object, "currentStep", endpoint),
      variants: variants(object.variants, endpoint),
    };
  }

  async captureFunnelPageView(
    input: FunnelPageViewInput,
    signal?: AbortSignal,
  ): Promise<FunnelPageViewResponse> {
    assertEventId(input.eventId);
    assertCredential(input.anonymousId, "anonymousId");
    if (input.variant !== "A" && input.variant !== "B") {
      throw new ApiClientError("variant must be A or B", "invalid_funnel_variant");
    }
    const endpoint = "/funnel-pageviews";
    const object = record(await this.request(endpoint, {
      method: "POST",
      body: input,
      ...(signal ? { signal } : {}),
    }), endpoint);
    return {
      accepted: booleanField(object, "accepted", endpoint),
      idempotent: booleanField(object, "idempotent", endpoint),
    };
  }

  async getSession(sessionId: string, token: string, signal?: AbortSignal): Promise<SessionResponse> {
    assertCredential(sessionId, "sessionId");
    assertCredential(token, "sessionToken");
    const endpoint = `/sessions/${encodeURIComponent(sessionId)}`;
    const object = record(await this.request(endpoint, { token, ...(signal ? { signal } : {}) }), endpoint);
    const answersValue = object.answers;
    if (!Array.isArray(answersValue)) throw new ApiProtocolError(endpoint, "missing answers array");
    const answers = answersValue.map((item) => {
      const answer = record(item, endpoint);
      if (!Array.isArray(answer.answerIds) || !answer.answerIds.every((id) => typeof id === "string")) {
        throw new ApiProtocolError(endpoint, "invalid answer IDs");
      }
      return {
        questionId: stringField(answer, "questionId", endpoint),
        answerIds: answer.answerIds,
        answerVersion: stringField(answer, "answerVersion", endpoint),
      };
    });
    return {
      id: stringField(object, "id", endpoint),
      status: stringField(object, "status", endpoint) as SessionResponse["status"],
      currentStep: numberField(object, "currentStep", endpoint),
      variants: variants(object.variants, endpoint),
      answers,
      resultReady: booleanField(object, "resultReady", endpoint),
    };
  }

  async saveAnswer(
    sessionId: string,
    token: string,
    questionId: string,
    input: SaveAnswerInput,
    signal?: AbortSignal,
  ): Promise<SaveAnswerResponse> {
    assertCredential(sessionId, "sessionId");
    assertCredential(token, "sessionToken");
    const endpoint = `/sessions/${encodeURIComponent(sessionId)}/answers/${encodeURIComponent(questionId)}`;
    const object = record(await this.request(endpoint, {
      method: "PUT",
      token,
      body: input,
      ...(signal ? { signal } : {}),
    }), endpoint);
    if (object.saved !== true) throw new ApiProtocolError(endpoint, "answer was not acknowledged");
    return { saved: true, currentStep: numberField(object, "currentStep", endpoint) };
  }

  async completeSession(sessionId: string, token: string, signal?: AbortSignal): Promise<CompleteSessionResponse> {
    assertCredential(sessionId, "sessionId");
    assertCredential(token, "sessionToken");
    const endpoint = `/sessions/${encodeURIComponent(sessionId)}/complete`;
    const object = record(await this.request(endpoint, {
      method: "POST",
      token,
      ...(signal ? { signal } : {}),
    }), endpoint);
    const route = object.route;
    if (route !== undefined && !isPersonalizedRecipeRoute(route)) {
      throw new ApiProtocolError(endpoint, "invalid personalized recipe route");
    }
    return {
      resultToken: stringField(object, "resultToken", endpoint),
      expiresAt: stringField(object, "expiresAt", endpoint),
      idempotent: booleanField(object, "idempotent", endpoint),
      ...(route ? { route } : {}),
    };
  }

  async captureLead(
    sessionId: string,
    token: string,
    input: CaptureLeadInput,
    signal?: AbortSignal,
  ): Promise<CaptureLeadResponse> {
    assertCredential(sessionId, "sessionId");
    assertCredential(token, "sessionToken");
    assertEventId(input.eventId);
    const endpoint = `/sessions/${encodeURIComponent(sessionId)}/lead`;
    const object = record(await this.request(endpoint, {
      method: "POST",
      token,
      body: input,
      ...(signal ? { signal } : {}),
    }), endpoint);
    const eventId = stringField(object, "eventId", endpoint);
    if (!UUID_V4_PATTERN.test(eventId)) throw new ApiProtocolError(endpoint, "invalid canonical lead event ID");
    return {
      leadId: stringField(object, "leadId", endpoint),
      eventId,
      resultToken: stringField(object, "resultToken", endpoint),
      resultUrl: stringField(object, "resultUrl", endpoint),
      preferenceUrl: stringField(object, "preferenceUrl", endpoint),
      preferenceTokenExpiresAt: stringField(object, "preferenceTokenExpiresAt", endpoint),
      idempotent: booleanField(object, "idempotent", endpoint),
    };
  }

  async updateWhatsApp(
    sessionId: string,
    token: string,
    input: UpdateWhatsAppInput,
    signal?: AbortSignal,
  ): Promise<UpdateWhatsAppResponse> {
    assertCredential(sessionId, "sessionId");
    assertCredential(token, "sessionToken");
    if (input.consent && !/^\s*\+?[\d()\s.-]{8,32}\s*$/.test(input.whatsapp)) {
      throw new ApiClientError("A valid WhatsApp number is required", "invalid_whatsapp");
    }
    assertEventId(input.eventId);
    const endpoint = `/sessions/${encodeURIComponent(sessionId)}/lead/whatsapp`;
    const object = record(await this.request(endpoint, {
      method: "PATCH",
      token,
      body: input,
      ...(signal ? { signal } : {}),
    }), endpoint);
    if (object.updated !== true) throw new ApiProtocolError(endpoint, "WhatsApp update was not acknowledged");
    return { updated: true, whatsappConsent: booleanField(object, "whatsappConsent", endpoint) };
  }

  async ingestEvents(
    sessionId: string,
    token: string,
    events: AnalyticsEventInput[],
    options: { signal?: AbortSignal; keepalive?: boolean } = {},
  ): Promise<IngestEventsResponse> {
    assertCredential(sessionId, "sessionId");
    assertCredential(token, "sessionToken");
    if (events.length === 0 || events.length > 20) {
      throw new ApiClientError("Analytics event batches must contain between 1 and 20 events", "invalid_event_batch");
    }
    for (const event of events) assertEventId(event.eventId);
    const endpoint = `/sessions/${encodeURIComponent(sessionId)}/events`;
    const object = record(await this.request(endpoint, {
      method: "POST",
      token,
      body: { events },
      ...(options.signal ? { signal: options.signal } : {}),
      ...(options.keepalive !== undefined ? { keepalive: options.keepalive } : {}),
    }), endpoint);
    return {
      accepted: numberField(object, "accepted", endpoint),
      duplicates: numberField(object, "duplicates", endpoint),
      analyticsConsent: booleanField(object, "analyticsConsent", endpoint),
    };
  }

  async ingestResultEvents(
    resultToken: string,
    events: AnalyticsEventInput[],
    options: { signal?: AbortSignal; keepalive?: boolean } = {},
  ): Promise<IngestEventsResponse> {
    assertCredential(resultToken, "resultToken");
    if (events.length === 0 || events.length > 20) {
      throw new ApiClientError("Analytics event batches must contain between 1 and 20 events", "invalid_event_batch");
    }
    for (const event of events) assertEventId(event.eventId);
    const endpoint = `/results/${encodeURIComponent(resultToken)}/events`;
    const object = record(await this.request(endpoint, {
      method: "POST",
      body: { events },
      ...(options.signal ? { signal: options.signal } : {}),
      ...(options.keepalive !== undefined ? { keepalive: options.keepalive } : {}),
    }), endpoint);
    return {
      accepted: numberField(object, "accepted", endpoint),
      duplicates: numberField(object, "duplicates", endpoint),
      analyticsConsent: booleanField(object, "analyticsConsent", endpoint),
    };
  }

  async updateTrackingConsent(
    sessionId: string,
    token: string,
    input: TrackingConsentInput,
    signal?: AbortSignal,
  ): Promise<Pick<TrackingConsentInput, "analytics" | "marketing">> {
    assertCredential(sessionId, "sessionId");
    assertCredential(token, "sessionToken");
    const endpoint = `/sessions/${encodeURIComponent(sessionId)}/consent`;
    const object = record(await this.request(endpoint, {
      method: "PATCH",
      token,
      body: input,
      ...(signal ? { signal } : {}),
    }), endpoint);
    return {
      analytics: booleanField(object, "analytics", endpoint),
      marketing: booleanField(object, "marketing", endpoint),
    };
  }

  async getResult(resultToken: string, signal?: AbortSignal): Promise<ResultResponse> {
    assertCredential(resultToken, "resultToken");
    const endpoint = `/results/${encodeURIComponent(resultToken)}`;
    const object = record(await this.request(endpoint, signal ? { signal } : {}), endpoint);
    const primaryObject = record(object.primary, endpoint);
    const secondaryObject = record(object.secondary, endpoint);
    const personalization = record(object.personalization, endpoint);
    const considerations = personalization.considerations;
    if (!Array.isArray(considerations) || !considerations.every((item) => typeof item === "string")) {
      throw new ApiProtocolError(endpoint, "invalid personalization considerations");
    }
    const answerRecord = personalization.answers === undefined ? {} : record(personalization.answers, endpoint);
    const answers: Record<string, string[]> = {};
    for (const [questionId, values] of Object.entries(answerRecord)) {
      if (!/^q(?:1[0-4]|[1-9])$/.test(questionId) || !Array.isArray(values) || !values.every((item) => typeof item === "string")) {
        throw new ApiProtocolError(endpoint, "invalid personalization answers");
      }
      answers[questionId] = values;
    }
    const funnelVariant = stringField(object, "funnelVariant", endpoint);
    if (funnelVariant !== "A" && funnelVariant !== "B") {
      throw new ApiProtocolError(endpoint, "invalid funnel variant");
    }
    const route = object.route;
    if (route !== undefined && !isPersonalizedRecipeRoute(route)) {
      throw new ApiProtocolError(endpoint, "invalid personalized recipe route");
    }
    return {
      funnelVariant,
      primary: { ...publicProfile(primaryObject, endpoint), affinity: numberField(primaryObject, "affinity", endpoint) },
      secondary: { ...publicProfile(secondaryObject, endpoint), affinity: numberField(secondaryObject, "affinity", endpoint) },
      personalization: {
        foodStyle: stringField(personalization, "foodStyle", endpoint),
        considerations,
        answers,
      },
      ...(route ? { route } : {}),
      quizVersion: stringField(object, "quizVersion", endpoint),
      algorithmVersion: stringField(object, "algorithmVersion", endpoint),
      expiresAt: stringField(object, "expiresAt", endpoint),
    };
  }

  async getOffer(resultToken: string, signal?: AbortSignal): Promise<OfferResponse> {
    assertCredential(resultToken, "resultToken");
    const endpoint = `/offers/${encodeURIComponent(resultToken)}`;
    const object = record(await this.request(endpoint, signal ? { signal } : {}), endpoint);
    const variant = stringField(object, "variant", endpoint);
    if (variant !== "A" && variant !== "B") throw new ApiProtocolError(endpoint, "invalid offer variant");
    return {
      profile: publicProfile(object.profile, endpoint),
      variant,
      checkoutAvailable: booleanField(object, "checkoutAvailable", endpoint),
    };
  }

  async createCheckout(
    sessionId: string,
    token: string,
    input: CreateCheckoutInput,
    signal?: AbortSignal,
  ): Promise<CreateCheckoutResponse> {
    assertCredential(sessionId, "sessionId");
    assertCredential(token, "sessionToken");
    assertEventId(input.eventId);
    const endpoint = `/sessions/${encodeURIComponent(sessionId)}/checkout`;
    const object = record(await this.request(endpoint, {
      method: "POST",
      token,
      body: input,
      ...(signal ? { signal } : {}),
    }), endpoint);
    return {
      checkoutId: stringField(object, "checkoutId", endpoint),
      checkoutUrl: stringField(object, "checkoutUrl", endpoint),
      reference: stringField(object, "reference", endpoint),
    };
  }

  async createCheckoutIntent(
    sessionId: string,
    token: string,
    input: CreateCheckoutIntentInput,
    signal?: AbortSignal,
  ): Promise<CreateCheckoutIntentResponse> {
    assertCredential(sessionId, "sessionId");
    assertCredential(token, "sessionToken");
    assertEventId(input.eventId);
    if (input.plan !== "monthly" && input.plan !== "annual") {
      throw new ApiClientError("plan must be monthly or annual", "invalid_checkout_plan");
    }
    const endpoint = `/sessions/${encodeURIComponent(sessionId)}/checkout-intent`;
    const object = record(await this.request(endpoint, {
      method: "POST",
      token,
      body: input,
      ...(signal ? { signal } : {}),
    }), endpoint);
    const eventId = stringField(object, "eventId", endpoint);
    if (!UUID_V4_PATTERN.test(eventId) || eventId !== input.eventId) {
      throw new ApiProtocolError(endpoint, "checkout intent event ID did not match the request");
    }
    return {
      checkoutId: stringField(object, "checkoutId", endpoint),
      intentId: stringField(object, "intentId", endpoint),
      checkoutUrl: stringField(object, "checkoutUrl", endpoint),
      reference: stringField(object, "reference", endpoint),
      eventId,
      idempotent: booleanField(object, "idempotent", endpoint),
    };
  }

  async createCheckoutIntentByResultToken(
    input: CreateCheckoutIntentInput,
    signal?: AbortSignal,
  ): Promise<CreateCheckoutIntentResponse> {
    assertCredential(input.resultToken, "resultToken");
    assertEventId(input.eventId);
    if (input.plan !== "monthly" && input.plan !== "annual") {
      throw new ApiClientError("plan must be monthly or annual", "invalid_checkout_plan");
    }
    const endpoint = "/checkout-intent";
    const object = record(await this.request(endpoint, {
      method: "POST",
      body: input,
      ...(signal ? { signal } : {}),
    }), endpoint);
    const eventId = stringField(object, "eventId", endpoint);
    if (!UUID_V4_PATTERN.test(eventId) || eventId !== input.eventId) {
      throw new ApiProtocolError(endpoint, "checkout intent event ID did not match the request");
    }
    return {
      checkoutId: stringField(object, "checkoutId", endpoint),
      intentId: stringField(object, "intentId", endpoint),
      checkoutUrl: stringField(object, "checkoutUrl", endpoint),
      reference: stringField(object, "reference", endpoint),
      eventId,
      idempotent: booleanField(object, "idempotent", endpoint),
    };
  }

  async getCheckoutStatus(reference: string, signal?: AbortSignal): Promise<CheckoutStatusResponse> {
    assertCredential(reference, "checkoutReference");
    const endpoint = `/checkout/${encodeURIComponent(reference)}`;
    const object = record(await this.request(endpoint, signal ? { signal } : {}), endpoint);
    const status = stringField(object, "status", endpoint);
    if (!["initiated", "pending", "approved", "failed", "refunded"].includes(status)) {
      throw new ApiProtocolError(endpoint, "invalid checkout status");
    }
    const purchasedAt = nullableStringField(object, "purchasedAt", endpoint);
    if (purchasedAt && Number.isNaN(Date.parse(purchasedAt))) {
      throw new ApiProtocolError(endpoint, "invalid purchase timestamp");
    }
    const amountMinor = nullableNumberField(object, "amountMinor", endpoint);
    if (amountMinor !== null && (!Number.isSafeInteger(amountMinor) || amountMinor < 0)) {
      throw new ApiProtocolError(endpoint, "invalid purchase amount");
    }
    const currency = nullableStringField(object, "currency", endpoint);
    if (currency !== null && !/^[A-Z]{3}$/.test(currency)) {
      throw new ApiProtocolError(endpoint, "invalid purchase currency");
    }
    const purchaseEventId = nullableStringField(object, "purchaseEventId", endpoint);
    if (purchaseEventId !== null && !UUID_V4_PATTERN.test(purchaseEventId)) {
      throw new ApiProtocolError(endpoint, "purchase event ID is not an RFC 4122 UUID v4");
    }
    const handoffUrl = nullableStringField(object, "handoffUrl", endpoint);
    if (handoffUrl) {
      try {
        const url = new URL(handoffUrl);
        if ((url.protocol !== "https:" && !(url.protocol === "http:" && url.hostname === "localhost")) || url.username || url.password) {
          throw new Error("unsafe handoff URL");
        }
      } catch (error) {
        throw new ApiProtocolError(endpoint, "invalid handoff URL", { cause: error });
      }
    }
    return {
      status: status as CheckoutStatusResponse["status"],
      purchasedAt,
      amountMinor,
      currency,
      purchaseEventId,
      handoffUrl,
    };
  }

  async getPreferences(preferenceToken: string, signal?: AbortSignal): Promise<PreferenceResponse> {
    assertCredential(preferenceToken, "preferenceToken");
    const endpoint = `/preferences/${encodeURIComponent(preferenceToken)}`;
    const object = record(await this.request(endpoint, signal ? { signal } : {}), endpoint);
    return {
      emailConsent: booleanField(object, "emailConsent", endpoint),
      whatsappConsent: booleanField(object, "whatsappConsent", endpoint),
      privacyVersion: stringField(object, "privacyVersion", endpoint),
      deleted: booleanField(object, "deleted", endpoint),
    };
  }

  async updatePreferences(
    preferenceToken: string,
    input: PreferenceUpdateInput,
    signal?: AbortSignal,
  ): Promise<PreferenceUpdateResponse> {
    assertCredential(preferenceToken, "preferenceToken");
    const endpoint = `/preferences/${encodeURIComponent(preferenceToken)}`;
    const object = record(await this.request(endpoint, {
      method: "PATCH",
      body: input,
      ...(signal ? { signal } : {}),
    }), endpoint);
    return {
      emailConsent: booleanField(object, "emailConsent", endpoint),
      whatsappConsent: booleanField(object, "whatsappConsent", endpoint),
    };
  }

  async deleteLead(preferenceToken: string, signal?: AbortSignal): Promise<DeleteLeadResponse> {
    assertCredential(preferenceToken, "preferenceToken");
    const endpoint = `/preferences/${encodeURIComponent(preferenceToken)}`;
    const object = record(await this.request(endpoint, {
      method: "DELETE",
      body: { confirm: true },
      ...(signal ? { signal } : {}),
    }), endpoint);
    if (object.status !== "completed") throw new ApiProtocolError(endpoint, "deletion was not completed");
    return { requestId: stringField(object, "requestId", endpoint), status: "completed" };
  }

  async getAdminMetrics(from: Date, to: Date, signal?: AbortSignal): Promise<AdminMetricsResponse> {
    const query = new URLSearchParams({ from: from.toISOString(), to: to.toISOString() });
    const endpoint = `/admin/metrics?${query.toString()}`;
    const object = record(await this.request(endpoint, signal ? { signal } : {}), endpoint);
    const range = record(object.range, endpoint);
    return {
      range: {
        from: stringField(range, "from", endpoint),
        to: stringField(range, "to", endpoint),
      },
      overview: metricRecord(object.overview, endpoint),
      profiles: metricRows(object.profiles, "profiles", endpoint),
      campaigns: metricRows(object.campaigns, "campaigns", endpoint),
      abandonment: metricRows(object.abandonment, "abandonment", endpoint),
      experiments: metricRows(object.experiments, "experiments", endpoint),
    };
  }

  async getAdminExportCsv(from: Date, to: Date, signal?: AbortSignal): Promise<string> {
    const query = new URLSearchParams({ from: from.toISOString(), to: to.toISOString() });
    const endpoint = `/admin/export.csv?${query.toString()}`;
    const timeoutMs = this.timeoutMs;
    const controller = new AbortController();
    let timedOut = false;
    const timer = globalThis.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);
    const onAbort = () => controller.abort(signal?.reason);
    if (signal?.aborted) controller.abort(signal.reason);
    else signal?.addEventListener("abort", onAbort, { once: true });
    try {
      const response = await this.fetcher(`${this.baseUrl}${endpoint}`, {
        headers: { Accept: "text/csv", "X-Request-Id": makeRequestId() },
        credentials: "same-origin",
        signal: controller.signal,
      });
      if (!response.ok) throw new ApiHttpError(response.status, `http_${response.status}`);
      return await response.text();
    } catch (error) {
      if (error instanceof ApiClientError) throw error;
      if (timedOut) throw new ApiTimeoutError(timeoutMs, { cause: error });
      if (signal?.aborted) throw new ApiAbortedError({ cause: error });
      throw new ApiNetworkError({ cause: error });
    } finally {
      globalThis.clearTimeout(timer);
      signal?.removeEventListener("abort", onAbort);
    }
  }
}

export function createApiClient(config: Pick<AppConfig, "apiBaseUrl" | "apiTimeoutMs"> = appConfig): ApiClient {
  return new ApiClient({ baseUrl: config.apiBaseUrl, timeoutMs: config.apiTimeoutMs });
}

export const apiClient = createApiClient();
